"use strict";

import Backbone from "backbone";
import Parse from "parse";
import config from "@/config";
import UserClient from "@/helpers/UserClient";

const TIERS = {
	FREE: "free",
	PLUS: "plus",
	PRO: "pro",
	PREMIUM: "premium",
};

const FEATURES = {
	SAVED_SETTINGS: "savedSettings",
	SKY_OBJECT_OBSERVATION: "skyObjectObservation",
	EQUIPMENT: "equipment",
	FOV_INDICATOR: "fovIndicator",
	OBSERVING_LIST: "observingList",
	OBSERVING_SESSION: "observingSession",
	OBSERVING_SITE: "observingSite",
};

// Maps tier to available features
const TIER_FEATURES = {
	[TIERS.FREE]: [FEATURES.SAVED_SETTINGS],
	[TIERS.PLUS]: Object.values(FEATURES),
	[TIERS.PRO]: Object.values(FEATURES),
	[TIERS.PREMIUM]: Object.values(FEATURES),
};

// Maps old get() keys to new feature names for backward compat
const LEGACY_KEY_MAP = {
	hasSavedSetting: FEATURES.SAVED_SETTINGS,
	hasSkyObjectObservation: FEATURES.SKY_OBJECT_OBSERVATION,
	hasEquipment: FEATURES.EQUIPMENT,
	hasFOVIndicator: FEATURES.FOV_INDICATOR,
	hasObservingList: FEATURES.OBSERVING_LIST,
	hasObservingSession: FEATURES.OBSERVING_SESSION,
	hasObservingSite: FEATURES.OBSERVING_SITE,
};

const SUBSCRIPTION_STORES = {
	STRIPE: "stripe",
};

const BETA_HOSTNAMES = ["dev.livesky.com", "localhost"];

const Access = {
	state: null,

	/**
	 * Initialize access permissions for the current user
	 * @returns {Promise<Object>} The Access object with initialized state
	 */
	init: async function () {
		try {
			const user = await this.getUser();
			const subscription = await this.fetchSubscription();
			const clientApps = await this.fetchClientApps(user);

			this.state = this.buildState(subscription, clientApps, user);

			// Emit event for UI layer to handle updates
			Backbone.trigger("access:initialized", this.state);

			return this;
		} catch (error) {
			Backbone.trigger("errors:parseReturned", error);
			throw error;
		}
	},

	/**
	 * Build immutable state object (pure function for testability)
	 * @param {Object} subscription - { hasPremium, store }
	 * @param {Array} clientApps - Array of client app IDs
	 * @param {Parse.User} user - User object for stripe IDs
	 * @returns {Object} Complete access state
	 */
	buildState: function (subscription, clientApps, user) {
		const { hasPremium, store } = subscription || {};
		const hasPlus = UserClient.hasPlusFeatures(clientApps);
		const hasPro = UserClient.hasProFeatures(clientApps);

		const tier = this.determineTier(hasPremium, hasPro, hasPlus);
		const features = this.buildFeatures(tier);
		const environment = this.buildEnvironment();

		return {
			tier,
			features,
			flags: { hasPremium: !!hasPremium, hasPlus, hasPro },
			environment,
			clientApps,
			subscriptionStore: store || null,
			// Stripe IDs populated separately if needed
			stripe: {
				customerId: null,
				subscriptionId: null,
				trialNotAvailable: false,
			},
		};
	},

	/**
	 * Determine user's tier based on subscription and client apps
	 */
	determineTier: function (hasPremium, hasPro, hasPlus) {
		if (hasPremium) return TIERS.PREMIUM;
		if (hasPro) return TIERS.PRO;
		if (hasPlus) return TIERS.PLUS;
		return TIERS.FREE;
	},

	/**
	 * Build features object based on tier
	 */
	buildFeatures: function (tier) {
		const availableFeatures = TIER_FEATURES[tier] || [];
		const features = {};
		Object.values(FEATURES).forEach((feature) => {
			features[feature] = availableFeatures.includes(feature);
		});
		return features;
	},

	/**
	 * Build environment flags
	 */
	buildEnvironment: function () {
		return {
			isBeta: BETA_HOSTNAMES.includes(location.hostname),
			isLocal: config.env === "local" || location.hostname === "localhost",
		};
	},

	// --- Data fetching methods ---

	getUser: async function () {
		const currentUser = Parse.User.current();
		if (!currentUser) {
			throw new Error("No authenticated user found");
		}
		try {
			const userQuery = new Parse.Query(Parse.User);
			return await userQuery.get(currentUser.id);
		} catch (error) {
			// Handle invalid session token - clear session and redirect to login
			if (error.code === Parse.Error.INVALID_SESSION_TOKEN) {
				console.warn("Invalid session token, logging out");
				await Parse.User.logOut();
				window.location.href = "/login.html";
				throw error;
			}
			throw error;
		}
	},

	fetchSubscription: async function () {
		try {
			return await Parse.Cloud.run("getUserSubscription");
		} catch (error) {
			Backbone.trigger("errors:parseReturned", error);
			return { hasPremium: false, store: null };
		}
	},

	fetchClientApps: async function (user) {
		if (!user) {
			throw new Error("User is required to get client information");
		}

		const query = new Parse.Query("UserClientInfo");
		query.equalTo("user", user);
		query.notEqualTo("deleted", true);

		try {
			const userClientInfo = await query.first();
			const clientApps = userClientInfo?.has("clients")
				? userClientInfo.get("clients").split(",")
				: [];

			// Merge legacy clients stored directly on user
			const userClients = user?.has("clients") ? user.get("clients") : [];
			return [...clientApps, ...userClients];
		} catch (error) {
			Backbone.trigger("errors:parseReturned", error);
			return [];
		}
	},

	/**
	 * Fetch and populate Stripe IDs (call after init if needed)
	 */
	fetchStripeIds: async function (user) {
		try {
			const subscriptionQuery = new Parse.Query("Subscription");
			subscriptionQuery.equalTo("user", user);
			const subscription = await subscriptionQuery.first();

			if (subscription && this.state) {
				this.state.stripe = {
					subscriptionId: subscription.get("stripeSubscriptionID") || null,
					customerId: subscription.get("stripeCustomerID") || null,
					trialNotAvailable: subscription.get("trial_not_available") || false,
				};
			}
			return this.state?.stripe || null;
		} catch (error) {
			Backbone.trigger("errors:parseReturned", error);
			return null;
		}
	},

	// --- Public API ---

	/**
	 * Check if user has at least the specified tier
	 * @param {string} tier - "free", "plus", "pro", or "premium"
	 */
	hasTier: function (tier) {
		if (!this.state) return false;
		const tierOrder = [TIERS.FREE, TIERS.PLUS, TIERS.PRO, TIERS.PREMIUM];
		const userTierIndex = tierOrder.indexOf(this.state.tier);
		const requiredTierIndex = tierOrder.indexOf(tier);
		if (userTierIndex === -1 || requiredTierIndex === -1) return false;
		return userTierIndex >= requiredTierIndex;
	},

	/**
	 * Check if user has a specific feature
	 * @param {string} feature - Feature key from FEATURES constant
	 */
	hasFeature: function (feature) {
		if (!this.state) return false;
		return this.state.features[feature] === true;
	},

	/**
	 * Check environment flag
	 * @param {string} env - "beta" or "local"
	 */
	isEnvironment: function (env) {
		if (!this.state) return false;
		if (env === "beta") return this.state.environment.isBeta;
		if (env === "local") return this.state.environment.isLocal;
		return false;
	},

	/**
	 * Get full state (for debugging)
	 */
	getState: function () {
		return this.state;
	},

	// --- Legacy backward-compat methods ---

	/**
	 * @deprecated Use hasTier(), hasFeature(), or isEnvironment() instead
	 * Backward compatible get() for existing code
	 */
	get: function (key) {
		if (!key || typeof key !== "string") {
			throw new Error("Access key must be a non-empty string");
		}

		if (!this.state) return undefined;

		// Map legacy keys to new API
		if (key === "hasPremium") return this.state.flags.hasPremium;
		if (key === "hasPlus") return this.state.flags.hasPlus;
		if (key === "hasPro") return this.state.flags.hasPro;
		if (key === "isBeta") return this.state.environment.isBeta;

		// Map feature keys
		const featureKey = LEGACY_KEY_MAP[key];
		if (featureKey) {
			return this.state.features[featureKey];
		}

		return undefined;
	},

	/**
	 * @deprecated Use isEnvironment("beta") instead
	 */
	isBeta: function () {
		return this.isEnvironment("beta");
	},
};

// Export constants for testing and external use
export { TIERS, FEATURES, TIER_FEATURES, SUBSCRIPTION_STORES };
export default Access;
